import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';

interface ExcelPreviewModalProps {
  url: string;
  onClose: () => void;
}

const ExcelPreviewModal: React.FC<ExcelPreviewModalProps> = ({ url, onClose }) => {
  const [html, setHtml] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sheets, setSheets] = useState<string[]>([]);
  const [activeSheet, setActiveSheet] = useState('');
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buffer = await res.arrayBuffer();

        // Create blob URL for download button
        const contentType = res.headers.get('Content-Type') || 'application/octet-stream';
        const blob = new Blob([buffer], { type: contentType });
        setBlobUrl(URL.createObjectURL(blob));

        const wb = XLSX.read(buffer, { type: 'array' });
        setWorkbook(wb);
        setSheets(wb.SheetNames);
        setActiveSheet(wb.SheetNames[0]);
        setHtml(XLSX.utils.sheet_to_html(wb.Sheets[wb.SheetNames[0]]));
      } catch (e: any) {
        setError(`ไม่สามารถโหลดไฟล์ได้: ${e?.message ?? ''} | URL: ${url}`);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [url]);

  const onSheetChange = (sheetName: string) => {
    if (!workbook) return;
    setActiveSheet(sheetName);
    setHtml(XLSX.utils.sheet_to_html(workbook.Sheets[sheetName]));
  };

  const fileName = decodeURIComponent(url.split('/').pop()?.split('?')[0] ?? '');

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#fff', display: 'flex', flexDirection: 'column' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #dee2e6', background: '#f8f9fa', flexShrink: 0 }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: '0 4px', color: '#495057' }}>✕</button>
        <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{fileName}</span>
        {blobUrl && (
          <a
            href={blobUrl}
            download={fileName}
            style={{ color: '#0d6efd', fontSize: 18, lineHeight: 1, padding: '0 4px', flexShrink: 0 }}
            title="ดาวน์โหลด"
          >
            <i className="fas fa-download" />
          </a>
        )}
      </div>

      {/* sheet tabs */}
      {sheets.length > 1 && (
        <div style={{ display: 'flex', overflowX: 'auto', borderBottom: '1px solid #dee2e6', padding: '4px 8px', gap: 4, background: '#f8f9fa', flexShrink: 0 }}>
          {sheets.map(s => (
            <button
              key={s}
              onClick={() => onSheetChange(s)}
              style={{
                padding: '3px 10px', fontSize: 12, border: 'none', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap',
                background: activeSheet === s ? '#0d6efd' : '#e9ecef',
                color: activeSheet === s ? '#fff' : '#495057',
              }}
            >{s}</button>
          ))}
        </div>
      )}

      {/* content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: '#6c757d' }}>
            <div>กำลังโหลด...</div>
          </div>
        )}
        {error && (
          <div style={{ textAlign: 'center', color: '#dc3545', padding: 40 }}>{error}</div>
        )}
        {!loading && !error && (
          <div
            dangerouslySetInnerHTML={{ __html: html }}
            style={{ fontSize: 12 }}
          />
        )}
      </div>

      <style>{`
        .excel-preview-modal table { border-collapse: collapse; width: 100%; }
        .excel-preview-modal td, .excel-preview-modal th { border: 1px solid #dee2e6; padding: 3px 6px; white-space: nowrap; }
      `}</style>
    </div>
  );
};

export default ExcelPreviewModal;
